import React, { useEffect, useRef, useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';

const supabase = createClient(
  'https://rbsrverouylthjdbrxgd.supabase.co',
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJic3J2ZXJvdXlsdGhqZGJyeGdkIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDQxNDQ1MTQsImV4cCI6MjA1OTcyMDUxNH0.KckvPMHcEWVHfKVNBRjLZENIsMi3uTXAsmvRXdrH74o'
);

function normalizeDigits(value) {
  return String(value)
    .replace(/[٠-٩]/g, (digit) =>
      String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit))
    )
    .replace(/[۰-۹]/g, (digit) =>
      String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit))
    );
}

function isApproved(status) {
  return (status || '')
    .trim()
    .startsWith('وردت الموافقة');
}

function todayInBerlin() {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());

  const get = (type) =>
    parts.find((part) => part.type === type).value;

  return `${get('year')}-${get('month')}-${get('day')}`;
}

function isValidDate(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) {
    return false;
  }

  const year = Number(value.slice(0, 4));

  if (year < 1900 || year > 9998) {
    return false;
  }

  const date = new Date(`${value}T00:00:00Z`);

  return (
    !Number.isNaN(date.getTime()) &&
    date.toISOString().slice(0, 10) === value
  );
}

// حساب ثلاثة أشهر تقويمية من تاريخ صدور الموافقة.
function approvalExpiry(value) {
  if (!isValidDate(value)) {
    return null;
  }

  const date = new Date(`${value}T00:00:00Z`);
  const year = date.getUTCFullYear();
  const targetMonth = date.getUTCMonth() + 3;

  const lastDay = new Date(
    Date.UTC(year, targetMonth + 1, 0)
  ).getUTCDate();

  return new Date(
    Date.UTC(
      year,
      targetMonth,
      Math.min(date.getUTCDate(), lastDay)
    )
  )
    .toISOString()
    .slice(0, 10);
}

function displayDate(value) {
  return value
    ? value.split('-').reverse().join('.')
    : '—';
}

function getStatusDetails(status) {
  if (isApproved(status)) {
    return {
      de: 'Die Genehmigung wurde erteilt.',
      ar: 'صدرت الموافقة.',
      kind: 'approved',
    };
  }

  const arabicStatus = (status || '')
    .split('\n')[0]
    .trim();

  const translations = {
    'جارى مراجعة الطلب. رجاء التحقق لاحقاً': {
      de: 'Ihr Antrag wird derzeit geprüft. Bitte prüfen Sie den Status später erneut.',
      ar: 'طلبكم قيد المراجعة حاليًا. يرجى التحقق مرة أخرى لاحقًا.',
      kind: 'review',
    },

    'لم ترد الموافقة': {
      de: 'Wir bedauern, Ihnen mitteilen zu müssen, dass Ihr Visumantrag abgelehnt wurde.',
      ar: 'نأسف لإبلاغكم بأنه قد تم رفض طلب التأشيرة.',
      kind: 'rejected',
    },

    'مطلوب إستيفاء': {
      de: 'Für die weitere Bearbeitung sind zusätzliche Angaben oder Unterlagen erforderlich. Bitte beachten Sie die Hinweise.',
      ar: 'يلزم استيفاء بيانات أو مستندات إضافية. يرجى الاطلاع على الملاحظات.',
      kind: 'required',
    },
  };

  return translations[arabicStatus] || {
    de: 'Der aktuelle Status kann nicht angezeigt werden. Bitte wenden Sie sich an das Generalkonsulat.',
    ar: 'يتعذر عرض الحالة الحالية. يرجى التواصل مع البعثة.',
    kind: 'info',
  };
}

async function generateUniqueBarcode() {
  const { data, error } = await supabase
    .from('visa_requests')
    .select('barcode');

  if (error) {
    throw error;
  }

  const existing = new Set(
    (data || []).map((item) => String(item.barcode))
  );

  const start = Math.floor(Math.random() * 9000);

  for (let offset = 0; offset < 9000; offset += 1) {
    const code = String(
      1000 + ((start + offset) % 9000)
    );

    if (!existing.has(code)) {
      return code;
    }
  }

  throw new Error(
    'Keine freie Antragsnummer verfügbar.'
  );
}

function ApprovalDetails({ issuedOn, today }) {
  const expiry = approvalExpiry(issuedOn);

  const validDate = Boolean(
    expiry && issuedOn <= today
  );

  const expired = validDate && expiry < today;

  return (
    <div className="approval-details">
      {validDate ? (
        <>
          <div className="date-grid">
            <div className="date-card">
              <span>
                Ausstellungsdatum der Genehmigung
              </span>

              <span className="ar" dir="rtl">
                تاريخ صدور الموافقة
              </span>

              <strong dir="ltr">
                {displayDate(issuedOn)}
              </strong>
            </div>

            <div className="date-card">
              <span>
                Genehmigung gültig bis einschließlich
              </span>

              <span className="ar" dir="rtl">
                الموافقة صالحة حتى نهاية يوم
              </span>

              <strong dir="ltr">
                {displayDate(expiry)}
              </strong>
            </div>
          </div>

          {expired ? (
            <div className="expiry-alert">
              <p lang="de">
                <strong>
                  Die Gültigkeit dieser Genehmigung ist
                  abgelaufen.
                </strong>
                {' '}
                Bitte wenden Sie sich an das
                Generalkonsulat, bevor Sie
                Originaldokumente einreichen.
              </p>

              <p className="ar" dir="rtl">
                <strong>
                  انتهت صلاحية هذه الموافقة.
                </strong>
                {' '}
                يرجى التواصل مع البعثة قبل تقديم
                المستندات الأصلية.
              </p>
            </div>
          ) : (
            <div className="submission-note">
              <p lang="de">
                Bitte reichen Sie Ihre
                Original-Reisepässe/Reisedokumente,
                Aufenthaltstitel und die erforderlichen
                Unterlagen rechtzeitig innerhalb der
                verbleibenden Gültigkeitsdauer der
                Genehmigung persönlich zu den angegebenen
                Zeiten oder per Einschreiben mit einem
                Rücksendeumschlag ein.
              </p>

              <p className="ar" dir="rtl">
                يرجى تقديم أصول جوازات السفر/وثائق السفر
                وتصاريح الإقامة والمستندات المطلوبة خلال
                المدة المتبقية من صلاحية الموافقة، شخصيًا
                في المواعيد المحددة أو بالبريد المسجل مع
                مظروف إعادة.
              </p>
            </div>
          )}
        </>
      ) : (
        <div className="expiry-alert">
          <p lang="de">
            Das Ausstellungsdatum der Genehmigung kann
            derzeit nicht verlässlich angezeigt werden.
            Bitte klären Sie die Gültigkeit mit dem
            Generalkonsulat, bevor Sie Originaldokumente
            einreichen.
          </p>

          <p className="ar" dir="rtl">
            يتعذر عرض تاريخ صدور الموافقة بصورة موثوقة
            حاليًا. يرجى التواصل مع البعثة للتأكد من
            صلاحية الموافقة قبل تقديم المستندات الأصلية.
          </p>
        </div>
      )}

      <div className="validity-note">
        <p lang="de">
          <strong>
            Die Genehmigung ist 3 Monate ab dem
            Ausstellungsdatum der Genehmigung gültig.
          </strong>
        </p>

        <p className="ar" dir="rtl">
          <strong>
            صلاحية الموافقة 3 أشهر من تاريخ صدور الموافقة.
          </strong>
        </p>
      </div>
    </div>
  );
}

export default function App() {
  const [barcode, setBarcode] = useState(null);

  const [numberLoading, setNumberLoading] =
    useState(true);

  const [numberError, setNumberError] =
    useState(false);

  const [isDownloading, setIsDownloading] =
    useState(false);

  const [downloadError, setDownloadError] =
    useState(false);

  const [trackInput, setTrackInput] = useState('');

  const [trackingResult, setTrackingResult] =
    useState(null);

  const [isTracking, setIsTracking] =
    useState(false);

  const [today, setToday] =
    useState(todayInBerlin);

  const trackingRef = useRef(false);
  const downloadingRef = useRef(false);
  const numberRequestRef = useRef(false);

  useEffect(() => {
    let active = true;

    generateUniqueBarcode()
      .then((code) => {
        if (active) {
          setBarcode(code);
        }
      })
      .catch(() => {
        if (active) {
          setNumberError(true);
        }
      })
      .finally(() => {
        if (active) {
          setNumberLoading(false);
        }
      });

    const timer = setInterval(() => {
      setToday(todayInBerlin());
    }, 60000);

    return () => {
      active = false;
      clearInterval(timer);
    };
  }, []);

  const retryNumber = async () => {
    if (numberRequestRef.current) {
      return;
    }

    numberRequestRef.current = true;
    setNumberLoading(true);
    setNumberError(false);

    try {
      setBarcode(await generateUniqueBarcode());
    } catch {
      setNumberError(true);
    } finally {
      numberRequestRef.current = false;
      setNumberLoading(false);
    }
  };

  const stampAndDownloadPDF = async () => {
    if (!barcode || downloadingRef.current) {
      return;
    }

    downloadingRef.current = true;
    setIsDownloading(true);
    setDownloadError(false);

    const applicationNumber = barcode;

    try {
      const response = await fetch(
        '/Visa Application Form.pdf'
      );

      if (!response.ok) {
        throw new Error(
          'PDF konnte nicht geladen werden.'
        );
      }

      const pdfDoc = await PDFDocument.load(
        await response.arrayBuffer()
      );

      const page = pdfDoc.getPages()[0];

      const font = await pdfDoc.embedFont(
        StandardFonts.Helvetica
      );

      page.drawText(
        `Antragsnummer: ${applicationNumber}`,
        {
          x: 50,
          y: 740,
          size: 16,
          font,
          color: rgb(0, 0, 0),
        }
      );

      const bytes = await pdfDoc.save();

      const url = URL.createObjectURL(
        new Blob([bytes], {
          type: 'application/pdf',
        })
      );

      const link = document.createElement('a');

      link.href = url;
      link.download =
        `Visumantrag_${applicationNumber}.pdf`;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      link.remove();

      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);

      setBarcode(null);
      setNumberLoading(true);
      setNumberError(false);

      try {
        setBarcode(await generateUniqueBarcode());
      } catch {
        setNumberError(true);
      } finally {
        setNumberLoading(false);
      }
    } catch {
      setDownloadError(true);
    } finally {
      downloadingRef.current = false;
      setIsDownloading(false);
    }
  };

  const trackStatus = async (event) => {
    event.preventDefault();

    if (trackingRef.current) {
      return;
    }

    const code = normalizeDigits(trackInput).trim();

    setTrackingResult(null);

    if (!/^\d{4}$/.test(code)) {
      setTrackingResult({
        de: 'Bitte geben Sie eine gültige vierstellige Antragsnummer ein.',
        ar: 'يرجى إدخال رقم طلب صحيح مكوّن من أربعة أرقام.',
        kind: 'info',
      });
      return;
    }

    trackingRef.current = true;
    setIsTracking(true);
    setToday(todayInBerlin());

    try {
      const { data, error } = await supabase
        .from('visa_requests')
        .select(
          'status, notes, approval_issued_on'
        )
        .eq('barcode', code)
        .maybeSingle();

      if (error) {
        throw error;
      }

      if (!data) {
        setTrackingResult({
          de: 'Unter dieser Antragsnummer wurde noch kein Antrag registriert. Bitte prüfen Sie die Nummer und den Versand Ihres Antrags.',
          ar: 'لم يُسجَّل طلب بهذا الرقم حتى الآن. يرجى مراجعة الرقم والتأكد من إرسال الطلب.',
          kind: 'info',
        });
        return;
      }

      setTrackingResult({
        ...getStatusDetails(data.status),
        approved: isApproved(data.status),
        issuedOn: data.approval_issued_on,
        notes: data.notes,
      });
    } catch {
      setTrackingResult({
        de: 'Der Status konnte nicht abgerufen werden. Bitte versuchen Sie es später erneut.',
        ar: 'تعذر الاستعلام عن الحالة. يرجى المحاولة لاحقًا.',
        kind: 'info',
      });
    } finally {
      trackingRef.current = false;
      setIsTracking(false);
    }
  };

  const resultKind = () => {
    if (!trackingResult?.approved) {
      return trackingResult?.kind || 'info';
    }

    const expiry = approvalExpiry(
      trackingResult.issuedOn
    );

    if (
      !expiry ||
      trackingResult.issuedOn > today ||
      expiry < today
    ) {
      return 'required';
    }

    return 'approved';
  };

  return (
    <>
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&family=Inter:wght@400;600;700;800&display=swap"
      />

      <style>{`
        body {
          margin: 0;
        }

        .visa-page {
          min-height: 100vh;
          padding: 30px 16px;
          background: linear-gradient(
            145deg,
            #eef5fb,
            #f8fafc,
            #eef8f2
          );
          font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
          color: #172033;
        }

        .visa-page * {
          box-sizing: border-box;
        }

        .visa-container {
          max-width: 790px;
          margin: 0 auto;
        }

        .visa-header {
          margin-bottom: 26px;
        }

        .visa-header h1 {
          margin: 0;
          color: #084f91;
          font-size: clamp(28px, 5vw, 44px);
        }

        .ar {
          font-family: 'Cairo', Arial, sans-serif;
          line-height: 1.9;
          text-align: right;
        }

        .ar-title {
          margin-top: 8px;
          color: #627185;
          font-size: 21px;
        }

        .visa-card {
          padding: 28px;
          margin-bottom: 24px;
          border: 1px solid #dce5ee;
          border-radius: 18px;
          background: white;
          box-shadow: 0 8px 28px #243e5910;
        }

        .visa-card h2 {
          margin: 0 0 6px;
          color: #153e65;
          font-size: 23px;
        }

        .section-ar {
          color: #647182;
          margin: 0 0 20px;
        }

        .application-number {
          padding: 16px;
          margin: 20px 0;
          border-radius: 12px;
          background: #eef6fd;
          border: 1px solid #c9e0f4;
        }

        .application-number strong {
          display: block;
          margin: 7px 0;
          color: #084f91;
          font-size: 30px;
          min-height: 36px;
        }

        .visa-page button {
          width: 100%;
          padding: 15px 18px;
          border: none;
          border-radius: 11px;
          background: #0a5dab;
          color: white;
          font: inherit;
          font-weight: 700;
          cursor: pointer;
        }

        .visa-page button.green {
          background: #197a48;
        }

        .visa-page button:disabled {
          opacity: .6;
          cursor: wait;
        }

        .button-ar {
          display: block;
          margin-top: 5px;
          font-size: 14px;
          text-align: center;
        }

        .visa-page label {
          display: block;
          margin-bottom: 10px;
          font-weight: 700;
        }

        .visa-page input {
          width: 100%;
          padding: 16px;
          border: 2px solid #c8d3df;
          border-radius: 11px;
          background: #fbfdff;
          font: inherit;
          font-size: 20px;
        }

        .visa-page input:focus {
          outline: 3px solid #197a4818;
          border-color: #197a48;
        }

        .input-help {
          margin: 8px 0 18px;
          color: #687789;
          font-size: 14px;
          line-height: 1.7;
        }

        .tracking-reminder {
          padding: 16px;
          margin: 20px 0;
          background: #f3f7fb;
          border-radius: 11px;
          font-size: 14px;
          color: #40556a;
          line-height: 1.8;
        }

        .tracking-reminder p {
          margin: 5px 0;
        }

        .status-box {
          margin-top: 24px;
          padding: 20px;
          border-left: 5px solid;
          border-radius: 13px;
          line-height: 1.7;
        }

        .status-approved {
          background: #edf9f1;
          border-color: #198754;
          color: #126638;
        }

        .status-review {
          background: #fff8df;
          border-color: #d39c14;
          color: #735408;
        }

        .status-rejected {
          background: #fff1f1;
          border-color: #c93636;
          color: #9b2424;
        }

        .status-required {
          background: #fff3e8;
          border-color: #dd7a24;
          color: #89470d;
        }

        .status-info {
          background: #eef6fd;
          border-color: #0a5dab;
          color: #164f7d;
        }

        .status-title {
          font-weight: 800;
          font-size: 18px;
        }

        .date-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          margin-top: 20px;
        }

        .date-card {
          padding: 15px;
          background: white;
          border: 1px solid #dce5ee;
          border-radius: 11px;
          color: #26384a;
        }

        .date-card span {
          display: block;
          font-size: 13px;
        }

        .date-card strong {
          display: block;
          margin-top: 10px;
          color: #084f91;
          font-size: 24px;
        }

        .validity-note,
        .submission-note,
        .expiry-alert,
        .request-notes {
          padding: 15px;
          margin-top: 15px;
          border-radius: 10px;
          background: #ffffffba;
          color: #37475a;
          font-size: 14px;
          overflow-wrap: anywhere;
        }

        .validity-note p,
        .submission-note p,
        .expiry-alert p {
          margin: 5px 0 12px;
        }

        .validity-note strong {
          font-weight: 700;
        }

        .expiry-alert {
          background: #fff0e3;
          color: #934900;
        }

        .request-notes {
          white-space: pre-wrap;
        }

        .error-message {
          margin-top: 15px;
          padding: 14px;
          background: #fff1f1;
          color: #a32929;
          border-radius: 10px;
          line-height: 1.8;
        }

        .error-message button {
          margin-top: 10px;
        }

        @media (max-width: 540px) {
          .visa-card {
            padding: 20px;
          }

          .visa-card h2 {
            font-size: 20px;
          }

          .date-grid {
            grid-template-columns: 1fr;
          }

          .status-box {
            padding: 15px;
          }
        }
      `}</style>

      <main className="visa-page">
        <div className="visa-container">
          <header className="visa-header">
            <h1>Visumantrag & Status</h1>

            <div
              className="ar ar-title"
              dir="rtl"
            >
              طلب التأشيرة والاستعلام عن الحالة
            </div>
          </header>

          <section className="visa-card">
            <h2>
              Visumantragsformular herunterladen
            </h2>

            <p
              className="ar section-ar"
              dir="rtl"
            >
              تحميل نموذج طلب التأشيرة
            </p>

            <div className="application-number">
              <span>
                Ihre persönliche Antragsnummer
              </span>

              <strong>
                {barcode
                  ? `#${barcode}`
                  : numberLoading
                    ? '…'
                    : '—'}
              </strong>

              <div className="ar" dir="rtl">
                رقم الطلب الخاص بكم
              </div>
            </div>

            <button
              type="button"
              onClick={stampAndDownloadPDF}
              disabled={
                !barcode ||
                isDownloading ||
                numberLoading
              }
            >
              {isDownloading
                ? 'Formular wird vorbereitet …'
                : 'Formular herunterladen'}

              <span className="ar button-ar">
                {isDownloading
                  ? 'جارٍ إعداد النموذج…'
                  : 'تحميل النموذج'}
              </span>
            </button>

            {numberError && (
              <div
                className="error-message"
                role="alert"
              >
                <div>
                  Die Antragsnummer konnte nicht
                  geladen werden. Bitte versuchen
                  Sie es erneut.
                </div>

                <div className="ar" dir="rtl">
                  تعذر تحميل رقم الطلب. يرجى
                  إعادة المحاولة.
                </div>

                <button
                  type="button"
                  onClick={retryNumber}
                  disabled={numberLoading}
                >
                  Erneut versuchen / إعادة المحاولة
                </button>
              </div>
            )}

            {downloadError && (
              <div
                className="error-message"
                role="alert"
              >
                <div>
                  Das Formular konnte nicht
                  heruntergeladen werden. Bitte
                  versuchen Sie es erneut.
                </div>

                <div className="ar" dir="rtl">
                  تعذر تحميل النموذج. يرجى
                  المحاولة مرة أخرى.
                </div>
              </div>
            )}
          </section>

          <section className="visa-card">
            <h2>
              Bearbeitungsstatus prüfen
            </h2>

            <p
              className="ar section-ar"
              dir="rtl"
            >
              الاستعلام عن حالة الطلب
            </p>

            <div className="tracking-reminder">
              <p lang="de">
                Wir empfehlen, den Status spätestens
                einen Monat nach Einreichung des
                Antrags erstmals online zu prüfen
                und anschließend regelmäßig
                nachzusehen. Warten Sie hierfür
                nicht ausschließlich auf eine
                persönliche Benachrichtigung.
              </p>

              <p lang="de">
                Dies ist keine Zusage einer
                Bearbeitung innerhalb eines Monats.
              </p>

              <p className="ar" dir="rtl">
                ننصح ببدء متابعة حالة الطلب أونلاين
                في موعد أقصاه شهر من تاريخ تقديمه،
                ثم المتابعة بانتظام، دون الاكتفاء
                بانتظار الإخطار الشخصي. ولا يعني
                ذلك أن البت في الطلب يتم خلال شهر.
              </p>
            </div>

            <form onSubmit={trackStatus}>
              <label htmlFor="application-number">
                Antragsnummer / رقم الطلب
              </label>

              <input
                id="application-number"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                dir="ltr"
                maxLength={4}
                value={trackInput}
                disabled={isTracking}
                onChange={(event) =>
                  setTrackInput(
                    normalizeDigits(event.target.value)
                      .replace(/\D/g, '')
                      .slice(0, 4)
                  )
                }
                placeholder="1234"
              />

              <div className="input-help">
                Bitte geben Sie die vierstellige
                Antragsnummer ohne das #-Zeichen ein.

                <div className="ar" dir="rtl">
                  أدخل رقم الطلب المكوّن من أربعة
                  أرقام دون علامة #.
                </div>
              </div>

              <button
                type="submit"
                className="green"
                disabled={isTracking}
              >
                {isTracking
                  ? 'Status wird geprüft …'
                  : 'Status prüfen'}

                <span className="ar button-ar">
                  {isTracking
                    ? 'جارٍ التحقق من الحالة…'
                    : 'الاستعلام عن الحالة'}
                </span>
              </button>
            </form>

            {trackingResult && (
              <div
                className={`status-box status-${resultKind()}`}
                role="status"
                aria-live="polite"
              >
                <div
                  className="status-title"
                  lang="de"
                >
                  {trackingResult.de}
                </div>

                <div
                  className="ar status-title"
                  dir="rtl"
                >
                  {trackingResult.ar}
                </div>

                {trackingResult.approved && (
                  <ApprovalDetails
                    issuedOn={trackingResult.issuedOn}
                    today={today}
                  />
                )}

                {trackingResult.notes && (
                  <div className="request-notes">
                    <strong>
                      Hinweise / ملاحظات
                    </strong>

                    <div dir="auto">
                      {trackingResult.notes}
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
      </main>
    </>
  );
}